/**
 * @fileoverview Global modal context component for managing modal dialogs.
 * This component provides a centralized state and API for opening and closing modal dialogs.
 *
 * @example
 * // Basic usage
 * <GlobalModalContext>
 *     <Button onClick={() => modalAPI.open({ render: () => <div>Modal Content</div> })}>
 *         Open Modal
 *     </Button>
 * </GlobalModalContext>
 */

import { PropsWithChildren, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import css from './GlobalModalContext.module.css'
import { Modal } from './Modal'
import { ModalAPI, ModalConfig, ModalContext } from './ModalContext'

/**
 * @function GlobalModalContext
 * @description Component for managing global modal dialogs.
 * @param {PropsWithChildren} children - The child components to wrap with the modal context.
 * @returns {JSX.Element} The GlobalModalContext component.
 */
export function GlobalModalContext({ children }: PropsWithChildren) {
    const [modalConfig, setModalConfig] = useState<ModalConfig>({
        render: () => <></>,
    })

    const [isOpen, setIsOpen] = useState<boolean>(false)

    const API: ModalAPI = {
        async open(modalConfig: ModalConfig) {
            setModalConfig(modalConfig)
            setIsOpen(true)
        },
        async close() {
            setIsOpen(false)
        },
    }

    function onConfirm() {
        setIsOpen(false)
        return Promise.resolve()
    }

    function onCancel() {
        setIsOpen(false)
        return Promise.resolve()
    }

    function onClose() {
        modalConfig.onClose?.()
        setIsOpen(false)
        return Promise.resolve()
    }

    return (
        <ModalContext.Provider value={API}>
            {children}
            <CSSTransition
                in={isOpen}
                timeout={300}
                mountOnEnter
                unmountOnExit
                classNames={{
                    enter: css.transition_enter,
                    enterActive: css.transition_enter_active,
                    exit: css.transition_exit,
                    exitActive: css.transition_exit_active,
                }}
            >
                <Modal
                    render={modalConfig.render}
                    canClose={modalConfig.canClose}
                    onConfirm={onConfirm}
                    onCancel={onCancel}
                    onClose={onClose}
                />
            </CSSTransition>
        </ModalContext.Provider>
    )
}
